import {
  LOYALTY_ACTIVITY_CODE_ORDER,
  LOYALTY_ACTIVITY_DEFAULT_POINTS,
  LOYALTY_ACTIVITY_LABELS
} from '@constants/loyalty.constant';
import { LOYALTY_POINTS_HISTORY_SOURCES } from '@wallet/component/loyalty-points/loyalty-points.interface';
import type { LoyaltyActivityCode, LoyaltyConfigActivity } from '@models/loyalty.interface';
import type {
  LoyaltyPointsActivityCode,
  LoyaltyPointsBundleId,
  LoyaltyPointsHistoryEntry,
  LoyaltyPointsHistorySource,
  LoyaltyPointsPackage,
  LoyaltyPointsPaymentData,
  LoyaltyPointsSummaryCard,
  LoyaltyPointsWalletState
} from '@wallet/component/loyalty-points/loyalty-points.interface';
import type { Button } from '@modules/dynamic-form-builder/model/formBuilderInterface';

/** Suffix kept on one line in info popovers (see info-icon `.loyalty-points-info-line-suffix`). */
export const formatFanLoyaltyActivityBulletLine = (label: string, points: number): string => {
  const pointWord = points === 1 ? 'point' : 'points';
  return `• ${label}<span class="loyalty-points-info-line-suffix">: ${points} ${pointWord}</span>`;
};

/** Default earning list from app constants (fallback when API is unavailable). */
export const buildFanLoyaltyEarningActivitiesList = (): string => {
  return LOYALTY_ACTIVITY_CODE_ORDER.map((code) => {
    const label = LOYALTY_ACTIVITY_LABELS[code];
    const pts = LOYALTY_ACTIVITY_DEFAULT_POINTS[code];
    return formatFanLoyaltyActivityBulletLine(label, pts);
  }).join('\n');
};

export const LOYALTY_POINTS_TEXT = {
  WALLET_TAB_LABEL: 'Loyalty Points',
  AVAILABLE_POINTS: 'Available Points',
  EARNED_POINTS: 'Earned Points',
  PURCHASED_POINTS: 'Purchased Points',
  AVAILABLE_POINTS_INFO:
    'These are the total loyalty points you have available to spend on unlocking content.',
  PURCHASED_POINTS_INFO: 'These are the points you purchased, which never expire.',
  EARNED_POINTS_INFO_INTRO: 'You can earn points by completing various activities in the community. Here\'s a list of activities you can complete to earn points:',
  EARNED_POINTS_INFO_NOTE:
    'Note: Earned Points must be used within 30 days as they will expire. Additionally to maintain certain merit based badges, you must continue to earn points.',
  EARNING_ACTIVITIES_NONE_ENABLED:
    'This creator does not have any point-earning activities enabled at the moment.',
  NEED_MORE_POINTS: 'Need More Points?',
  BUY_POINTS_BUTTON: 'BUY POINTS',
  ACTIVITY: 'Activity',
  POINTS: 'Points',
  EXPIRY_DATE: 'Expiration Date',
  NEVER_EXPIRES: 'Never Expires',
  NO_EXPIRY: '-',
  POINTS_SUFFIX: 'Points',
  DAILY_LOGIN_BONUS: 'Daily Login',
  COMMENT_FEED_ACTIVITY: 'Commenting on a Feed Post',
  COMMENT_CHAT_ACTIVITY: 'Commenting in the Chat Room',
  COMMENT_ACTIVITY: 'You have earned [points] [pointLabel] for commenting',
  ENGAGE_CONTENT_ACTIVITY: 'Watching or Listening to Video, Podcast or Music Portfolio Content',
  SHARE_CONTENT_ACTIVITY: 'Sharing a Piece of Content',
  SUPPORT_CREATOR_ACTIVITY: 'Supporting by Spending Money',
  FAN_RECRUITMENT_ACTIVITY: 'Recruiting a New Fan Registration',
  UNLOCKED_PREMIUM_CONTENT: 'Unlocked Premium Content',
  REDEEMED_POINTS: 'Unlocked Premium Content',
  REFUNDED_POINTS: 'Refunded points',
  EXPIRED_POINTS: 'Expired points',
  PURCHASED_POINTS_PACKAGE: 'Purchased [points] Points',
  POINT_LABEL_SINGULAR: 'point',
  POINT_LABEL_PLURAL: 'points',
  COIN_ALT: 'Points coin',
  PAYMENT_TITLE: 'Loyalty Points Purchase',
  PAYMENT_DESCRIPTION: 'Buy loyalty points for [creatorName]',
  PAYMENT_PACKAGE_LABEL: 'Points Package',
  PAYMENT_PACKAGE_TYPE: 'Individual Purchase',
  PAYMENT_CALL_TYPE_LABEL: 'loyalty points',
  CURRENCY_CODE: 'USD',
  CURRENCY_DISPLAY: 'symbol',
  CURRENCY_DIGITS: '1.2-2'
} as const;

export const buildFanLoyaltyEarningActivitiesListFromServerActivities = (
  serverActivities: LoyaltyConfigActivity[]
): string => {
  const byCode = new Map<
    LoyaltyActivityCode,
    { label: string; points: number; is_active: boolean }
  >();

  LOYALTY_ACTIVITY_CODE_ORDER.forEach((code) => {
    byCode.set(code, {
      label: LOYALTY_ACTIVITY_LABELS[code],
      points: LOYALTY_ACTIVITY_DEFAULT_POINTS[code],
      is_active: false
    });
  });

  (serverActivities || []).forEach((item) => {
    const code = item.activity_code;
    if (!code || !byCode.has(code)) {
      return;
    }
    const row = byCode.get(code)!;
    row.points = item.points_earned ?? LOYALTY_ACTIVITY_DEFAULT_POINTS[code];
    row.is_active = !!item.is_active;
  });

  const activeLines = LOYALTY_ACTIVITY_CODE_ORDER.filter((code) => byCode.get(code)!.is_active).map(
    (code) => {
      const row = byCode.get(code)!;
      return formatFanLoyaltyActivityBulletLine(row.label, row.points);
    }
  );

  if (!activeLines.length) {
    return LOYALTY_POINTS_TEXT.EARNING_ACTIVITIES_NONE_ENABLED;
  }
  return activeLines.join('\n');
};

export const buildEarnedPointsInfoMessage = (listLines: string): string => {
  return [
    LOYALTY_POINTS_TEXT.EARNED_POINTS_INFO_INTRO,
    '',
    listLines,
    '',
    LOYALTY_POINTS_TEXT.EARNED_POINTS_INFO_NOTE
  ].join('\n');
};

export const getDefaultEarnedPointsInfoMessage = (): string =>
  buildEarnedPointsInfoMessage(buildFanLoyaltyEarningActivitiesList());

export type LoyaltyPointsSummaryCardsOptions = {
  earnedPointsInfoMessage?: string;
};

export const LOYALTY_POINTS_BUNDLE_TOTALS: Record<LoyaltyPointsBundleId, number> = {
  BUNDLE_500: 500,
  BUNDLE_1100: 1100,
  BUNDLE_2300: 2300,
  BUNDLE_4500: 4500,
  BUNDLE_9000: 9000
};

export const LOYALTY_POINTS_ACTIVITY_LABELS: Record<LoyaltyPointsActivityCode, string> = {
  COMMENT: LOYALTY_POINTS_TEXT.COMMENT_ACTIVITY,
  FAN_LOGIN: LOYALTY_POINTS_TEXT.DAILY_LOGIN_BONUS,
  COMMENT_FEED: LOYALTY_POINTS_TEXT.COMMENT_FEED_ACTIVITY,
  COMMENT_CHAT: LOYALTY_POINTS_TEXT.COMMENT_CHAT_ACTIVITY,
  ENGAGE_CONTENT: LOYALTY_POINTS_TEXT.ENGAGE_CONTENT_ACTIVITY,
  SUPPORT_CREATOR: LOYALTY_POINTS_TEXT.SUPPORT_CREATOR_ACTIVITY,
  FAN_RECRUITMENT: LOYALTY_POINTS_TEXT.FAN_RECRUITMENT_ACTIVITY,
  SHARE_CONTENT: LOYALTY_POINTS_TEXT.SHARE_CONTENT_ACTIVITY
};

export const POSITIVE_LOYALTY_POINTS_HISTORY_SOURCES: LoyaltyPointsHistorySource[] = [
  LOYALTY_POINTS_HISTORY_SOURCES.EARNED,
  LOYALTY_POINTS_HISTORY_SOURCES.PURCHASED,
  LOYALTY_POINTS_HISTORY_SOURCES.REFUNDED
];

export const LOYALTY_POINTS_BUY_BUTTON: Button = {
  type: 'button',
  class: 'btn btn-primary loyalty-points-buy-button',
  label: LOYALTY_POINTS_TEXT.BUY_POINTS_BUTTON,
  emClass: 'icon-newpost-plus',
  name: 'buyPoints'
};

const getPointLabel = (points: number): string => {
  return Math.abs(points) === 1
    ? LOYALTY_POINTS_TEXT.POINT_LABEL_SINGULAR
    : LOYALTY_POINTS_TEXT.POINT_LABEL_PLURAL;
};

const replaceHistoryTextValues = (label: string, points: number): string => {
  return label
    .replace('[points]', Math.abs(points).toString())
    .replace('[pointLabel]', getPointLabel(points));
};

const LOYALTY_POINTS_SOURCE_LABELS: Record<
  Exclude<
    LoyaltyPointsHistorySource,
    | typeof LOYALTY_POINTS_HISTORY_SOURCES.EARNED
    | typeof LOYALTY_POINTS_HISTORY_SOURCES.PURCHASED
  >,
  string
> = {
  [LOYALTY_POINTS_HISTORY_SOURCES.REFUNDED]: LOYALTY_POINTS_TEXT.REFUNDED_POINTS,
  [LOYALTY_POINTS_HISTORY_SOURCES.REDEEMED]: LOYALTY_POINTS_TEXT.REDEEMED_POINTS,
  [LOYALTY_POINTS_HISTORY_SOURCES.EXPIRED]: LOYALTY_POINTS_TEXT.EXPIRED_POINTS
};

const getEarnedHistoryLabel = (historyEntry: LoyaltyPointsHistoryEntry): string => {
  return replaceHistoryTextValues(
    LOYALTY_POINTS_ACTIVITY_LABELS[historyEntry.activity_code as LoyaltyPointsActivityCode],
    historyEntry.points
  );
};

export const getLoyaltyPointsSummaryCards = (
  loyaltyPointsState: LoyaltyPointsWalletState,
  options?: LoyaltyPointsSummaryCardsOptions
): LoyaltyPointsSummaryCard[] => {
  const earnedInfoMessage =
    options?.earnedPointsInfoMessage ?? getDefaultEarnedPointsInfoMessage();

  return [
    {
      label: LOYALTY_POINTS_TEXT.AVAILABLE_POINTS,
      infoMessage: LOYALTY_POINTS_TEXT.AVAILABLE_POINTS_INFO,
      value: loyaltyPointsState.current_points_balance
    },
    {
      label: LOYALTY_POINTS_TEXT.EARNED_POINTS,
      infoMessage: earnedInfoMessage,
      value: loyaltyPointsState.earned_points_balance
    },
    {
      label: LOYALTY_POINTS_TEXT.PURCHASED_POINTS,
      infoMessage: LOYALTY_POINTS_TEXT.PURCHASED_POINTS_INFO,
      value: loyaltyPointsState.purchased_points_balance
    }
  ];
};

export const getLoyaltyPointsPackageLabel = (
  loyaltyPointsPackage: Pick<LoyaltyPointsPackage, 'total_points'>
): string => {
  return `${loyaltyPointsPackage.total_points.toLocaleString()} ${LOYALTY_POINTS_TEXT.POINTS_SUFFIX}`;
};

export const getLoyaltyPointsHistoryLabel = (
  historyEntry: LoyaltyPointsHistoryEntry
): string => {
  switch (historyEntry.source) {
    case LOYALTY_POINTS_HISTORY_SOURCES.PURCHASED:
      return LOYALTY_POINTS_TEXT.PURCHASED_POINTS_PACKAGE.replace(
        '[points]',
        LOYALTY_POINTS_BUNDLE_TOTALS[historyEntry.bundle_id as LoyaltyPointsBundleId].toLocaleString()
      );
    case LOYALTY_POINTS_HISTORY_SOURCES.REFUNDED:
    case LOYALTY_POINTS_HISTORY_SOURCES.REDEEMED:
    case LOYALTY_POINTS_HISTORY_SOURCES.EXPIRED:
      return LOYALTY_POINTS_SOURCE_LABELS[historyEntry.source];
    case LOYALTY_POINTS_HISTORY_SOURCES.EARNED:
    default:
      return getEarnedHistoryLabel(historyEntry);
  }
};

export const getLoyaltyPointsPaymentDescription = (creatorName: string): string => {
  return LOYALTY_POINTS_TEXT.PAYMENT_DESCRIPTION.replace('[creatorName]', creatorName);
};

export const getLoyaltyPointsPaymentData = (
  orderSK: string,
  loyaltyPointsPackage: LoyaltyPointsPackage,
  creatorUser: LoyaltyPointsPaymentData['user'],
  callType: string
): LoyaltyPointsPaymentData => {
  return {
    amount: loyaltyPointsPackage.base_price,
    localPrice: loyaltyPointsPackage.price,
    exchangeRateUSD: loyaltyPointsPackage.exchange_rate_to_usd,
    currency_symbol: loyaltyPointsPackage.currency_symbol,
    orderSK,
    callType,
    plan_name: getLoyaltyPointsPackageLabel(loyaltyPointsPackage),
    points: loyaltyPointsPackage.total_points,
    bundle_id: loyaltyPointsPackage.bundle_id,
    user: creatorUser
  };
};
